/**
 * DrivePanel - Vista tipo Outlook con tabla y scroll funcional garantizado
 * Versión simplificada con estructura probada de ChatPanel
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowUpDown,
  Loader2,
  Eye,
  Trash2,
  RefreshCw,
  Plus,
  RotateCcw as RotateCcwIcon,
  HardDrive,
  ChevronLeft,
  ChevronRight,
  Settings,
  AlertCircle,
  Upload,
  Users,
  FileText,
  X,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import {
  apiService,
  type Document as ApiDocument,
  type PDFSnippet,
  type SavedMessage,
} from '../services/api';
// import type { Project as ApiProject } from '../services/api'; // REMOVED
// import ProjectSelector from './ProjectSelector'; // REMOVED
import { eventBus, EventNames } from '../utils/eventBus';
import type { SavedContent } from '../services/savedContentService';
import { type ColumnConfig } from '../hooks/useColumnManager';
import DriveToolbar from './DriveToolbar';
import DriveTableBody from './drive/DriveTableBody';
import DriveFilterDropdown from './drive/DriveFilterDropdown';
import DrivePeriodDropdown from './drive/DrivePeriodDropdown';
import DriveColumnSettingsMenu from './drive/DriveColumnSettingsMenu';
import ConceptualSummaryModal from './ConceptualSummaryModal';
// Hooks
import { useDriveData } from '../hooks/useDriveData';
import { useDriveSelection } from '../hooks/useDriveSelection';
import { useDriveFilters } from '../hooks/useDriveFilters';
import { useDriveSorting, type SortOption } from '../hooks/useDriveSorting';
import { useDriveUpload } from '../hooks/useDriveUpload';
import { useDriveBulkActions } from '../hooks/useDriveBulkActions';
import { useDriveColumns } from '../hooks/useDriveColumns';
import { useDocumentSelection } from '../hooks/useDocumentSelection';
// New hooks for refactoring
import {
  useDriveContextSync,
  useDriveOriginalValues,
  useDriveDropdowns,
  useDriveTableItems,
  useDriveItemHandlers,
  useDriveSelectionHandlers,
  useDriveContextMenu,
} from './drive/hooks';
// Services and utilities
// Note: transformToTableItems, getUniqueFilterValues, applyDriveFilters now used in useDriveTableItems hook
import type { TableItem } from '../services/driveService';
import { formatProcessingTime, formatProcessingMethods } from '../utils/driveFormatters';
import { DriveTableCell } from './drive/DriveTableCell';
import type { PanelTabType } from './PanelToolSelector';
import type { MinimalDocument } from '../contexts/DocumentSelectionContext';

type DriveSnippet = PDFSnippet & {
  markdown?: string;
  front_matter?: Record<string, any>;
  parsed_content?: string;
};

interface DrivePanelProps {
  project?: any;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  documents?: any[];
  // selectedDocuments and onDocumentSelect removed - now using DocumentSelectionContext
  onDocumentUpload?: (document: any) => void;
  onDocumentView?: (document: any) => void;
  onDocumentDelete?: (document: ApiDocument) => Promise<void> | void;
  onDocumentReprocess?: (documentId: string) => void;
  // projectId removed - project system eliminated
  onSnippetClick?: (snippet: DriveSnippet) => void;
  onSavedMessageView?: (message: SavedMessage) => void;
  onStudioResultView?: (result: any) => void;
  onResetWidth?: () => void;
  // projects removed
  // currentProject removed
  // onSelectProject removed
  // onCreateProject removed
  // onEditProject removed
  // onDeleteProject removed
  panelPosition?: 'left' | 'center' | 'right';
  onPanelChange?: (panel: PanelTabType) => void;
  availablePanels?: PanelTabType[];
  currentPanel?: PanelTabType;
  onClose?: () => void;
  onNavigateBack?: () => void;
  onNavigateForward?: () => void;
  canGoBack?: boolean;
  canGoForward?: boolean;
}

const DrivePanel: React.FC<DrivePanelProps> = ({
  project,
  documents = [],
  // selectedDocuments and onDocumentSelect removed - using context
  onDocumentUpload,
  onDocumentView,
  onDocumentDelete,
  onDocumentReprocess,
  // projectId removed
  onSnippetClick,
  onSavedMessageView,
  onStudioResultView,
  isCollapsed = false,
  onToggleCollapse,
  onResetWidth,
  // projects = [], // REMOVED
  // currentProject = null, // REMOVED
  // onSelectProject, // REMOVED
  // onCreateProject, // REMOVED
  // onEditProject, // REMOVED
  // onDeleteProject, // REMOVED
  panelPosition,
  onPanelChange,
  availablePanels,
  currentPanel,
  onClose,
  onNavigateBack,
  onNavigateForward,
  canGoBack = false,
  canGoForward = false,
}) => {
  const { theme } = useTheme();
  const canManageProjects = false; // project system removed
  const currentUser = apiService.getCurrentUser();

  // Use DocumentSelectionContext for selection management
  const {
    selectedIds: selectedDocumentsFromContext,
    selectDocuments: selectContextDocuments,
    deselectDocuments: deselectContextDocuments,
    setDocument: setContextDocument,
    setAvailableDocumentIds,
  } = useDocumentSelection();

  // ============================================================================
  // COLUMN MANAGEMENT
  // ============================================================================
  const {
    columns,
    visibleColumns,
    toggleColumnVisibility,
    reorderColumns,
    resizeColumn,
    resetToDefaults,
    getColumn,
    isDragging,
    draggedColumnId,
    dragOverColumnId,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    resizingColumnId,
    handleResizeStart,
    isColumnSettingsOpen,
    setIsColumnSettingsOpen,
    columnSettingsMenuPosition,
    setColumnSettingsMenuPosition,
    columnSettingsMenuRef,
    columnSettingsButtonRef,
  } = useDriveColumns();

  const closeColumnSettingsMenu = useCallback(() => {
    setIsColumnSettingsOpen(false);
    setColumnSettingsMenuPosition(null);
  }, [setIsColumnSettingsOpen, setColumnSettingsMenuPosition]);

  // ============================================================================
  // HOOKS - Data, Selection, Filters, Sorting, Upload
  // ============================================================================

  // Filters hook - initialize with empty arrays, will be updated
  const [tempPrivateDocs, setTempPrivateDocs] = useState<ApiDocument[]>([]);
  const [tempSharedDocs, setTempSharedDocs] = useState<ApiDocument[]>([]);

  // Context sync hook - needs ref wrapper for document select
  // We'll create this after we have the selection hook
  const selectedDocumentsFromContextRef = useRef(selectedDocumentsFromContext);
  useEffect(() => {
    selectedDocumentsFromContextRef.current = selectedDocumentsFromContext;
  }, [selectedDocumentsFromContext]);

  const handleDocumentSelectRef = useRef<(documentIds: string[]) => void>();

  // Selection hook - now uses context internally
  const selection = useDriveSelection({
    selectedDocuments: selectedDocumentsFromContext,
    onDocumentSelect: (documentIds: string[]) => {
      handleDocumentSelectRef.current?.(documentIds);
    },
    // projectId removed
  });

  // Filters hook - needs documents and selection setter
  // Initialize with empty arrays, will be updated from data hook
  const filters = useDriveFilters({
    privateDocuments: tempPrivateDocs,
    sharedDocuments: tempSharedDocs,
    // projectId removed
    onDocumentSelect: (documentIds: string[]) => {
      handleDocumentSelectRef.current?.(documentIds);
    },
    setInternalSelectedDocuments: selection.setInternalSelectedDocuments,
  });

  // Data hook - now with filters from filters hook
  const data = useDriveData({
    documents,
    debouncedSearchTerm: filters.debouncedSearchTerm,
    searchInContent: filters.searchInContent,
    advancedFilters: filters.advancedFilters,
  });

  // Update temp docs when data changes
  useEffect(() => {
    setTempPrivateDocs(data.privateDocuments);
    setTempSharedDocs(data.sharedDocuments);
  }, [data.privateDocuments, data.sharedDocuments]);

  // Context sync hook - now that we have data
  const contextSync = useDriveContextSync({
    privateDocuments: data.privateDocuments,
    sharedDocuments: data.sharedDocuments,
    selectedDocumentsFromContext,
    selectContextDocuments,
    deselectContextDocuments,
    setContextDocument,
    setAvailableDocumentIds,
  });

  // Update ref so filters hook can call it
  handleDocumentSelectRef.current = contextSync.handleDocumentSelect;

  // Original values hook
  const { originalCompanyNames, originalPeriods } = useDriveOriginalValues({
    privateDocuments: data.privateDocuments,
    sharedDocuments: data.sharedDocuments,
  });

  // Dropdowns hook
  const dropdowns = useDriveDropdowns();

  // Sorting hook - must be declared before useDriveTableItems
  const sorting = useDriveSorting();

  // Table items hook
  const tableItemsData = useDriveTableItems({
    privateDocuments: data.privateDocuments,
    sharedDocuments: data.sharedDocuments,
    savedMessages: data.savedMessages,
    snippets: data.snippets,
    studioResults: data.studioResults,
    originalCompanyNames,
    originalPeriods,
    filterItemType: filters.filterItemType,
    filterPeriod: filters.filterPeriod,
    filterDocType: filters.filterDocType,
    filterCompany: filters.filterCompany,
    debouncedSearchTerm: filters.debouncedSearchTerm,
    searchInContent: filters.searchInContent,
    sortItems: sorting.sortItems,
  });

  // Item handlers hook
  const itemHandlers = useDriveItemHandlers({
    onDocumentView,
    onSnippetClick,
    onSavedMessageView,
    onStudioResultView,
    onDocumentReprocess,
    updateDocumentStatus: data.updateDocumentStatus,
    loadPrivateDocuments: data.loadPrivateDocuments,
    loadSharedDocuments: data.loadSharedDocuments,
    loadSavedData: data.loadSavedData,
  });

  // Selection handlers hook
  const selectionHandlers = useDriveSelectionHandlers({
    tableItems: tableItemsData.tableItems,
    selectedDocumentsFromContext,
    internalSelectedDocuments: selection.internalSelectedDocuments,
    setInternalSelectedDocuments: selection.setInternalSelectedDocuments,
    selectContextDocuments,
    deselectContextDocuments,
    setContextDocument,
    createMinimalDocumentFromTableItem: contextSync.createMinimalDocumentFromTableItem,
    handleDocumentToggle: selection.handleDocumentToggle,
    currentUser,
  });

  // Context menu hook
  const contextMenu = useDriveContextMenu();

  // Upload hook
  const upload = useDriveUpload({
    // projectId removed
    onDocumentUpload,
    loadPrivateDocuments: data.loadPrivateDocuments,
    loadSharedDocuments: data.loadSharedDocuments,
  });

  // Destructure for easier access
  const {
    privateDocuments,
    sharedDocuments,
    savedMessages,
    snippets,
    studioResults,
    loadPrivateDocuments,
    loadSharedDocuments,
    loadSavedData,
    setPrivateDocuments,
    setSharedDocuments,
    setSavedMessages,
    setSnippets,
    setStudioResults,
  } = data;

  const {
    internalSelectedDocuments,
    setInternalSelectedDocuments,
    handleDocumentToggle,
    handleSelectAll,
    handleDeselectAll,
  } = selection;

  const { sortBy, setSortBy, sortItems, handleSort, resetSort } = sorting;

  // Handlers using extracted hooks
  const {
    handleItemOpen,
    handleDocumentReprocess: handleDocumentReprocessFromHook,
    handleSnippetDelete,
    handleSavedMessageDelete,
    handleStudioResultDelete,
    handleRefreshDocuments,
    handleViewConceptualSummary: handleViewConceptualSummaryFromHook,
  } = itemHandlers;

  const {
    handleDocumentToggleWrapper,
    handleSelectAllItems,
    selectedItems,
    deletableItems,
    reprocessableItems,
    checkboxState,
  } = selectionHandlers;

  const { selectedTableItemsCount, allSelected, someSelected } = checkboxState;

  const handleClearSearch = useCallback(() => {
    filters.setSearchTerm('');
  }, [filters]);

  const toggleSearchInContent = useCallback(() => {
    filters.setSearchInContent(!filters.searchInContent);
  }, [filters]);

  // Estados de upload están en el hook useDriveUpload
  // Usar valores del hook upload
  const {
    isDragOver,
    isUploading,
    uploadProgress,
    uploadError,
    fileInputRef,
    fileInputSharedRef,
    handleFileUpload,
    handleFileDragOver,
    handleFileDragLeave,
    handleFileDrop,
    handleFileSelect,
    handleFileSelectShared,
    handleFileInputChange,
    setUploadError,
  } = upload;

  // Estado para modal de resumen conceptual
  const [conceptualSummaryModal, setConceptualSummaryModal] = useState<{
    isOpen: boolean;
    documentName: string;
    conceptualSummary: string | null;
  }>({
    isOpen: false,
    documentName: '',
    conceptualSummary: null,
  });

  const handleViewConceptualSummary = useCallback(
    (doc: ApiDocument) => {
      const modalData = handleViewConceptualSummaryFromHook(doc);
      setConceptualSummaryModal(modalData);
    },
    [handleViewConceptualSummaryFromHook]
  );

  const handleCloseConceptualSummaryModal = useCallback(() => {
    setConceptualSummaryModal({
      isOpen: false,
      documentName: '',
      conceptualSummary: null,
    });
  }, []);

  // Función para resetear filtros
  const handleReset = useCallback(() => {
    filters.setFilterItemType('all');
    filters.setFilterPeriod('all');
    filters.setFilterDocType('all');
    filters.setFilterCompany('all');
    resetSort();
    filters.setSearchTerm('');
  }, [filters, resetSort]);

  // Escuchar creación y eliminación de mensajes/snippets guardados para refrescar la tabla
  useEffect(() => {
    const handleSavedItemCreated = (event: { type: string }) => {
      // projectId removed - always refresh for current user
      loadSavedData();
    };

    const handleSavedItemDeleted = (event: { type: string }) => {
      // projectId removed - always refresh for current user
      loadSavedData();
    };

    eventBus.on(EventNames.SAVED_ITEM_CREATED, handleSavedItemCreated);
    eventBus.on(EventNames.SAVED_ITEM_DELETED, handleSavedItemDeleted);
    return () => {
      eventBus.off(EventNames.SAVED_ITEM_CREATED, handleSavedItemCreated);
      eventBus.off(EventNames.SAVED_ITEM_DELETED, handleSavedItemDeleted);
    };
  }, []); // loadSavedData es estable, no necesita estar en dependencias

  // ============================================================================
  // PREPARAR DATOS PARA LA TABLA
  // ============================================================================

  // Extract from hooks
  const { uniqueDocTypes, uniqueCompanies, uniquePeriods, tableItems, activeFiltersCount } =
    tableItemsData;

  // ============================================================================
  // SELECCIÓN MÚLTIPLE
  // ============================================================================
  // Handled by useDriveSelectionHandlers hook

  const { handleBulkDelete, handleBulkReprocess, bulkActionMessage, isBulkActionLoading } =
    useDriveBulkActions({
      deletableItems,
      reprocessableItems,
      loadPrivateDocuments: data.loadPrivateDocuments,
      loadSharedDocuments: data.loadSharedDocuments,
      loadSavedData: data.loadSavedData,
      setPrivateDocuments: data.setPrivateDocuments,
      setSharedDocuments: data.setSharedDocuments,
      setSavedMessages: data.setSavedMessages,
      setSnippets: data.setSnippets,
      setStudioResults: data.setStudioResults,
      setInternalSelectedDocuments: selection.setInternalSelectedDocuments,
      onDocumentDelete,
    });

  // ============================================================================
  // CELL RENDERER - Usar componente DriveTableCell
  // ============================================================================

  const renderCellContent = (column: ColumnConfig, item: TableItem, processingSummary?: string) => {
    // Usar el componente extraído
    return (
      <DriveTableCell
        column={column}
        item={item}
        processingSummary={processingSummary}
        privateDocuments={privateDocuments}
        sharedDocuments={sharedDocuments}
        setPrivateDocuments={setPrivateDocuments}
        setSharedDocuments={setSharedDocuments}
      />
    );
  };

  // ============================================================================
  // MENÚ CONTEXTUAL
  // ============================================================================
  // Handled by useDriveContextMenu hook
  const { openMenuId, menuPosition, menuButtonRefs, menuRefs, setOpenMenuId } = contextMenu;

  // Referencia para el botón de configuración en el header
  const headerSettingsButtonRef = useRef<HTMLButtonElement | null>(null);

  const handleOpenColumnSettingsFromOptions = useCallback(() => {
    const rect = headerSettingsButtonRef.current?.getBoundingClientRect();
    if (rect) {
      setColumnSettingsMenuPosition({ x: rect.left, y: rect.bottom });
      setIsColumnSettingsOpen(true);
    }
  }, [setColumnSettingsMenuPosition, setIsColumnSettingsOpen]);

  // Cerrar menú de columnas al hacer click fuera
  useEffect(() => {
    if (!isColumnSettingsOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        columnSettingsMenuRef.current &&
        !columnSettingsMenuRef.current.contains(event.target as Node)
      ) {
        setIsColumnSettingsOpen(false);
        setColumnSettingsMenuPosition(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isColumnSettingsOpen, columnSettingsMenuRef]);

  const panelContent = (
    <div
      className={`h-full w-full flex flex-col ${theme.colors.bg.primary} ${
        isDragOver ? 'ring-2 ring-blue-500' : ''
      }`}
      onDragOver={handleFileDragOver}
      onDragLeave={handleFileDragLeave}
      onDrop={handleFileDrop}
    >
      {/* Panel header removed — close via AppHeader "Volver", settings via column bar */}

      {/* Input oculto para seleccionar archivos privados */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.txt"
        onChange={(e) => handleFileInputChange(e, false)}
        className="hidden"
      />
      {/* Input oculto para seleccionar archivos compartidos (solo admin) */}
      {currentUser?.is_admin && (
        <input
          ref={fileInputSharedRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.txt"
          onChange={(e) => handleFileInputChange(e, true)}
          className="hidden"
        />
      )}

      {/* Upload Error */}
      {uploadError && (
        <div
          className={`mx-3 mb-2 p-3 ${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg flex items-center space-x-2`}
        >
          <AlertCircle className={`w-5 h-5 ${theme.colors.action.danger}`} />
          <p className={`text-sm ${theme.colors.action.danger}`}>{uploadError}</p>
          <button
            onClick={() => setUploadError(null)}
            className={`ml-auto ${theme.colors.text.secondary} hover:${theme.colors.text.primary}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Upload Progress */}
      {uploadProgress && (
        <div
          className={`mx-3 mb-2 p-3 ${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg`}
        >
          <div className={`text-sm ${theme.colors.text.primary} mb-2`}>
            {uploadProgress.filename}
          </div>
          <div className={`w-full ${theme.colors.bg.tertiary} rounded-full h-2`}>
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress.progress}%` }}
            ></div>
          </div>
          <div className={`text-xs ${theme.colors.text.secondary} mt-1`}>
            {uploadProgress.status}
          </div>
        </div>
      )}

      <DriveFilterDropdown
        theme={theme}
        isOpen={dropdowns.isFilterDropdownOpen}
        anchorRef={dropdowns.filterDropdownRef}
        menuRef={dropdowns.filterDropdownMenuRef}
        filters={{
          filterItemType: filters.filterItemType,
          filterPeriod: filters.filterPeriod,
          filterDocType: filters.filterDocType,
          filterCompany: filters.filterCompany,
          setFilterItemType: filters.setFilterItemType,
          setFilterPeriod: filters.setFilterPeriod,
          setFilterDocType: filters.setFilterDocType,
          setFilterCompany: filters.setFilterCompany,
        }}
        uniqueDocTypes={uniqueDocTypes}
        uniqueCompanies={uniqueCompanies}
        uniquePeriods={uniquePeriods}
        activeFiltersCount={activeFiltersCount}
        onClearFilters={() => dropdowns.toggleFilterDropdown()}
        onClose={() => dropdowns.toggleFilterDropdown()}
      />
      <DrivePeriodDropdown
        theme={theme}
        isOpen={dropdowns.isPeriodDropdownOpen}
        anchorRef={dropdowns.periodDropdownRef}
        menuRef={dropdowns.periodDropdownMenuRef}
        uniquePeriods={uniquePeriods}
        currentPeriod={filters.filterPeriod}
        onSelectPeriod={(period) => {
          filters.setFilterPeriod(period);
          dropdowns.togglePeriodDropdown();
        }}
        onClose={() => dropdowns.togglePeriodDropdown()}
      />

      {/* Contenedor con scroll horizontal y vertical */}
      <div
        className="flex-1 overflow-x-auto overflow-y-auto scrollbar-thin"
        style={{ minWidth: 0 }}
      >
        {/* Encabezado de tabla tipo Outlook - sticky (includes Settings button relocated from removed header) */}
        <div
          className={`sticky top-0 z-10 flex items-center gap-2 px-3 py-2 border-b ${theme.colors.border.primary} ${theme.colors.bg.primary} font-semibold text-xs min-w-max`}
        >
          {/* Settings button — relocated from removed panel header */}
          {!isCollapsed && (
            <button
              ref={headerSettingsButtonRef}
              type="button"
              onClick={handleOpenColumnSettingsFromOptions}
              className={`min-w-[28px] min-h-[28px] p-1 rounded-md ${theme.colors.text.secondary} hover:${theme.colors.bg.tertiary} transition-colors flex items-center justify-center`}
              title="Configuración de columnas"
            >
              <Settings className="w-3.5 h-3.5" />
            </button>
          )}
          <DriveColumnSettingsMenu
            theme={theme}
            isOpen={isColumnSettingsOpen}
            position={columnSettingsMenuPosition}
            menuRef={columnSettingsMenuRef}
            buttonRef={columnSettingsButtonRef}
            columns={columns}
            toggleColumnVisibility={toggleColumnVisibility}
            resetToDefaults={() => {
              resetToDefaults();
              closeColumnSettingsMenu();
            }}
            onClose={closeColumnSettingsMenu}
          />

          {/* Columnas dinámicas */}
          {visibleColumns.map((column, index) => {
            const isDragged = draggedColumnId === column.id;
            const isDragOver = dragOverColumnId === column.id;
            const isLastColumn = index === visibleColumns.length - 1;

            if (column.id === 'checkbox') {
              return (
                <div
                  key={column.id}
                  className={`flex-shrink-0 relative ${
                    !isLastColumn
                      ? 'after:content-[""] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-[1px] after:bg-nb-border/60'
                      : ''
                  }`}
                  style={{ width: column.defaultWidth }}
                >
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={handleSelectAllItems}
                    className="w-4 h-4 flex-shrink-0 cursor-pointer"
                    title={allSelected ? 'Deseleccionar todos' : 'Seleccionar todos'}
                  />
                </div>
              );
            }

            if (column.id === 'menu') {
              return (
                <div
                  key={column.id}
                  className="flex-shrink-0"
                  style={{ width: column.defaultWidth }}
                ></div>
              );
            }

            return (
              <div
                key={column.id}
                draggable={!column.alwaysVisible}
                onDragStart={(e) => handleDragStart(e, column.id)}
                onDragOver={(e) => handleDragOver(e, column.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, column.id)}
                onDragEnd={handleDragEnd}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Set menu position to mouse position
                  setColumnSettingsMenuPosition({ x: e.clientX, y: e.clientY });
                  setIsColumnSettingsOpen(true);
                }}
                onClick={() => column.sortKey && handleSort(column.sortKey as any)}
                className={`flex-shrink-0 relative group flex items-center gap-1 ${
                  theme.colors.text.primary
                } ${column.sortKey ? 'cursor-pointer' : ''} ${isDragged ? 'opacity-50' : ''} ${
                  isDragOver ? 'border-l-2 border-blue-500' : ''
                } ${
                  !isLastColumn
                    ? 'after:content-[""] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-[1px] after:bg-nb-border/60'
                    : ''
                }`}
                style={{ width: column.defaultWidth }}
              >
                <span className={column.id === 'shortName' ? 'text-xs' : ''}>{column.label}</span>
                {column.sortKey && <ArrowUpDown className="w-3 h-3" />}
                {column.resizable && (
                  <div
                    className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-nb-accent/50 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    onMouseDown={(e) => handleResizeStart(e, column.id)}
                    style={{ marginRight: '-4px', cursor: 'col-resize' }}
                    title="Arrastra para redimensionar"
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Área de items */}
        <DriveTableBody
          theme={theme}
          visibleColumns={visibleColumns}
          tableItems={tableItems}
          internalSelectedDocuments={selection.internalSelectedDocuments}
          handleDocumentToggle={handleDocumentToggleWrapper}
          handleItemOpen={handleItemOpen}
          renderCellContent={renderCellContent}
          currentUser={currentUser}
          formatProcessingTime={formatProcessingTime}
          formatProcessingMethods={formatProcessingMethods}
          isLoadingPrivate={data.isLoadingPrivate}
          isLoadingShared={data.isLoadingShared}
          openMenuId={openMenuId}
          setOpenMenuId={setOpenMenuId}
          menuPosition={menuPosition}
          menuRefs={menuRefs}
          menuButtonRefs={menuButtonRefs}
          onDocumentReprocess={handleDocumentReprocessFromHook}
          onDocumentDelete={onDocumentDelete}
          onViewConceptualSummary={handleViewConceptualSummary}
          onSnippetDelete={handleSnippetDelete}
          onSavedMessageDelete={handleSavedMessageDelete}
          onStudioResultDelete={handleStudioResultDelete}
        />
      </div>

      {/* Modal de resumen conceptual */}
      <ConceptualSummaryModal
        isOpen={conceptualSummaryModal.isOpen}
        onClose={handleCloseConceptualSummaryModal}
        documentName={conceptualSummaryModal.documentName}
        conceptualSummary={conceptualSummaryModal.conceptualSummary}
      />

      {/* Sección de búsqueda y filtros - Inferior fija (mismo formato que ChatInputArea) */}
      <div
        className={`flex-shrink-0 border-t ${theme.colors.border.primary} py-4 w-full`}
        style={{
          paddingLeft: 'clamp(1rem, 4vw, 2.5rem)',
          paddingRight: 'clamp(1rem, 4vw, 2.5rem)',
          boxSizing: 'border-box',
        }}
      >
        <div
          className="mx-auto"
          style={{ width: '100%', maxWidth: '95%', boxSizing: 'border-box' }}
        >
          <DriveToolbar
            theme={theme}
            searchTerm={filters.searchTerm}
            onSearchChange={filters.setSearchTerm}
            onClearSearch={handleClearSearch}
            bulkActionMessage={bulkActionMessage}
            selectedCount={selection.internalSelectedDocuments.length}
            isBulkActionLoading={isBulkActionLoading}
            onClearSelection={() => {
              if (!isBulkActionLoading && selectedDocumentsFromContext.length > 0) {
                deselectContextDocuments(selectedDocumentsFromContext);
              }
            }}
            onFileSelect={handleFileSelect}
            onFileSelectShared={currentUser?.is_admin ? handleFileSelectShared : undefined}
            isUploading={isUploading}
            isAdmin={currentUser?.is_admin || false}
            filterDropdownRef={dropdowns.filterDropdownRef}
            onToggleFilterDropdown={dropdowns.toggleFilterDropdown}
            periodDropdownRef={dropdowns.periodDropdownRef}
            onTogglePeriodDropdown={dropdowns.togglePeriodDropdown}
            searchInContent={filters.searchInContent}
            toggleSearchInContent={toggleSearchInContent}
          />
        </div>
      </div>
    </div>
  );

  return panelContent;
};

// Memoize DrivePanel to prevent unnecessary remounts
// DrivePanel manages its own data fetching and selection through context
// Use default shallow comparison for props
export default React.memo(DrivePanel);
